import React, { useState } from 'react';
import { Alert, IconButton, Tooltip, Typography } from '@mui/material';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';

import Appbar from 'components/appbar/Appbar';
import { moneyFormat } from 'helpers/moneyFormat';
import { getPercent } from './dashboardMetrics';
import ExecutiveDashboardFilterBox from './ExecutiveDashboardFilterBox';
import ExecutiveDashboardSkeleton from './ExecutiveDashboardSkeleton';
import {
  AnalyticsGrid,
  DashboardSurface,
  Donut,
  DonutCenter,
  EmptyState,
  FinancialGrid,
  FinancialItem,
  KpiCard,
  KpiGrid,
  LegendItem,
  Panel,
  PanelHeader,
  PanelTotal,
  RankingList,
  StatusBody,
  StatusLegend,
  TimelineBar,
  TimelineChart,
  TimelineColumn,
  TimelineLabel,
  TimelineTrack,
  TimelineValue,
} from './ExecutiveDashboardStyles';
import { renderRanking } from './RankingRows';
import { useExecutiveDashboard } from './hooks/useExecutiveDashboard';

const ExecutiveDashboard: React.FC = () => {
  const [showValues, setShowValues] = useState(true);
  const {
    guides,
    surgeries,
    closedSurgeries,
    openSurgeries,
    loading,
    hasError,
    period,
    periodError,
    handlePeriodChange,
    handleSearchPeriod,
    guideRevenue,
    soldRevenue,
    openPipeline,
    conversionRate,
    averageTicket,
    averageGuide,
    totalRevenue,
    procedureRanking,
    doctorRanking,
    timeline,
    timelineMax,
    rankingMax,
  } = useExecutiveDashboard();

  const hiddenValue = '••••';
  const displayMetric = (value: string | number) => (showValues ? value : hiddenValue);
  const displayMoney = (value: number) => (showValues ? moneyFormat(value) : hiddenValue);
  const displayPercent = (value: number) => (showValues ? `${value.toFixed(1)}%` : hiddenValue);
  const appbarActions = (
    <Tooltip title={showValues ? 'Ocultar valores' : 'Visualizar valores'}>
      <IconButton color="inherit" onClick={() => setShowValues(!showValues)}>
        {showValues ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
      </IconButton>
    </Tooltip>
  );

  if (loading) {
    return (
      <>
        <Appbar title="Dashboard Executivo" ActionsComponent={appbarActions} />
        <DashboardSurface>
          <ExecutiveDashboardFilterBox
            filter={period}
            handleChange={handlePeriodChange}
            handleSearch={handleSearchPeriod}
            loading={loading}
          />
          <ExecutiveDashboardSkeleton />
        </DashboardSurface>
      </>
    );
  }

  return (
    <>
      <Appbar title="Dashboard Executivo" ActionsComponent={appbarActions} />

      <DashboardSurface>
        <ExecutiveDashboardFilterBox
          filter={period}
          handleChange={handlePeriodChange}
          handleSearch={handleSearchPeriod}
          loading={loading}
        />

        {periodError && <Alert severity="warning">{periodError}</Alert>}
        {hasError && <Alert severity="error">Não foi possível carregar os indicadores do dashboard.</Alert>}

        <KpiGrid>
          <KpiCard>
            <Typography variant="caption">Consultas</Typography>
            <Typography variant="h4">{displayMetric(guides.length)}</Typography>
            <Typography variant="body2">{displayMoney(averageGuide)} média</Typography>
          </KpiCard>

          <KpiCard>
            <Typography variant="caption">Receita consultas</Typography>
            <Typography variant="h4">{displayMoney(guideRevenue)}</Typography>
            <Typography variant="body2">{displayMetric(guides.length)} registros</Typography>
          </KpiCard>

          <KpiCard>
            <Typography variant="caption">Vendas</Typography>
            <Typography variant="h4">{displayMetric(closedSurgeries.length)}</Typography>
            <Typography variant="body2">{displayMoney(averageTicket)} ticket</Typography>
          </KpiCard>

          <KpiCard>
            <Typography variant="caption">Receita cirúrgica</Typography>
            <Typography variant="h4">{displayMoney(soldRevenue)}</Typography>
            <Typography variant="body2">{displayMetric(closedSurgeries.length)} fechamentos</Typography>
          </KpiCard>

          <KpiCard>
            <Typography variant="caption">Conversão</Typography>
            <Typography variant="h4">{displayPercent(conversionRate)}</Typography>
            <Typography variant="body2">
              {displayMetric(closedSurgeries.length)} / {displayMetric(surgeries.length)}
            </Typography>
          </KpiCard>

          <KpiCard>
            <Typography variant="caption">Pipeline aberto</Typography>
            <Typography variant="h4">{displayMoney(openPipeline)}</Typography>
            <Typography variant="body2">{displayMetric(openSurgeries.length)} oportunidades</Typography>
          </KpiCard>
        </KpiGrid>

        <AnalyticsGrid>
          <Panel $wide>
            <PanelHeader>
              <Typography variant="subtitle1">Movimento financeiro</Typography>
              <PanelTotal>{displayMoney(totalRevenue)}</PanelTotal>
            </PanelHeader>

            <TimelineChart>
              {timeline.length ? (
                timeline.map(item => (
                  <TimelineColumn key={item.date} title={`${item.records} registros`}>
                    <TimelineValue>{displayMoney(item.amount)}</TimelineValue>
                    <TimelineTrack>
                      <TimelineBar style={{ height: `${Math.max((item.amount / timelineMax) * 100, 4)}%` }} />
                    </TimelineTrack>
                    <TimelineLabel>{item.date}</TimelineLabel>
                  </TimelineColumn>
                ))
              ) : (
                <EmptyState>Sem movimento no período</EmptyState>
              )}
            </TimelineChart>
          </Panel>

          <Panel>
            <PanelHeader>
              <Typography variant="subtitle1">Orçamentos</Typography>
              <PanelTotal>{displayMetric(surgeries.length)}</PanelTotal>
            </PanelHeader>

            <StatusBody>
              <Donut
                $closed={getPercent(closedSurgeries.length, surgeries.length)}
                aria-label={`${conversionRate.toFixed(1)}% de conversão`}
              >
                <DonutCenter>
                  <strong>{displayPercent(conversionRate)}</strong>
                  <span>conversão</span>
                </DonutCenter>
              </Donut>
              <StatusLegend>
                <LegendItem $color="#18864b">
                  <span>Fechados</span>
                  <strong>{displayMetric(closedSurgeries.length)}</strong>
                </LegendItem>
                <LegendItem $color="#d8a13b">
                  <span>Abertos</span>
                  <strong>{displayMetric(openSurgeries.length)}</strong>
                </LegendItem>
                <LegendItem $color="#841449">
                  <span>Ticket médio</span>
                  <strong>{displayMoney(averageTicket)}</strong>
                </LegendItem>
              </StatusLegend>
            </StatusBody>
          </Panel>

          <Panel>
            <PanelHeader>
              <Typography variant="subtitle1">Procedimentos</Typography>
              <PanelTotal>Top 5</PanelTotal>
            </PanelHeader>
            <RankingList>
              {renderRanking(procedureRanking, 'Sem vendas no período', showValues, rankingMax)}
            </RankingList>
          </Panel>

          <Panel>
            <PanelHeader>
              <Typography variant="subtitle1">Médicos</Typography>
              <PanelTotal>Top 5</PanelTotal>
            </PanelHeader>
            <RankingList>
              {renderRanking(doctorRanking, 'Sem movimento no período', showValues, rankingMax)}
            </RankingList>
          </Panel>

          <Panel>
            <PanelHeader>
              <Typography variant="subtitle1">Resumo financeiro</Typography>
              <PanelTotal>{displayMoney(totalRevenue)}</PanelTotal>
            </PanelHeader>
            <FinancialGrid>
              <FinancialItem>
                <span>Consultas</span>
                <strong>{displayMoney(guideRevenue)}</strong>
              </FinancialItem>
              <FinancialItem>
                <span>Cirurgias</span>
                <strong>{displayMoney(soldRevenue)}</strong>
              </FinancialItem>
              <FinancialItem>
                <span>Ticket cirurgia</span>
                <strong>{displayMoney(averageTicket)}</strong>
              </FinancialItem>
              <FinancialItem>
                <span>Média consulta</span>
                <strong>{displayMoney(averageGuide)}</strong>
              </FinancialItem>
            </FinancialGrid>
          </Panel>
        </AnalyticsGrid>
      </DashboardSurface>
    </>
  );
};

export default ExecutiveDashboard;
